import { useState, useEffect } from 'react';
import { productList as allProducts } from '../../../common';
import { downloadExcel } from '../../../../utils/excel';

/**
 * Editable price-list grid state: builds the rows from the segmentation (storeType.products),
 * keeps margin / suggested price / cost in sync while editing, and exports to Excel.
 */
export function usePriceListEditor({ products, storeType, setProducts, priceList, converted }) {
    const [dataSource, setDataSource] = useState([]);
    const [initialized, setInitialized] = useState(false);

    // Initialize dataSource ONLY when storeType or priceList changes, NOT when products changes
    useEffect(() => {
        // First time loading: set products from priceList if available
        if (!converted && priceList?.products && !initialized) {
            setProducts(priceList.products);
        }

        const data = storeType?.products?.map((productId) => {
            const productInfo = allProducts?.find((p) => p?.value === productId);

            // Check priceList first (for existing data), then products (for updates), then dataSource (for current edits)
            let existingProduct;
            const currentProduct = dataSource.find((d) => d.productId === productId);

            // If we already have data in dataSource (user is editing), keep it
            // BUT if converted is true (just uploaded Excel), use the new values
            if (currentProduct && initialized && !converted) {
                console.log('[PriceList] Keeping existing dataSource value for:', productId);
                return currentProduct;
            }

            if (!converted && priceList?.products) {
                existingProduct = priceList.products.find((p) => p?.productId === productId);
            } else {
                existingProduct = products?.find((p) => p?.productId === productId);
            }

            return {
                key: productId,
                productId,
                productName: productInfo?.name || productId,
                price: parseFloat(existingProduct?.price) || 0,
                suggestedPrice: parseFloat(existingProduct?.suggestedPrice) || 0,
                suggestedMargin:
                    existingProduct?.price && existingProduct?.suggestedPrice
                        ? parseFloat((((existingProduct?.suggestedPrice - existingProduct?.price) / existingProduct?.suggestedPrice) * 100).toFixed(2))
                        : 0
            };
        });
        setDataSource(data || []);
        setInitialized(true);
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [storeType, priceList, converted, products]);

    // Update products when dataSource changes
    const updateProduct = (productId, field, value) => {
        const updatedData = dataSource.map((item) => {
            if (item.productId === productId) {
                // Allow null/undefined values, don't force to 0 immediately
                const numValue = value === null || value === undefined || value === '' ? null : parseFloat(value);
                const newItem = { ...item, [field]: numValue === null ? 0 : numValue };

                // Recalculate dependent fields
                if (field === 'suggestedMargin') {
                    // margin changed: recalculate suggested price from price and margin
                    const margin = numValue || 0;
                    if (margin < 100 && newItem.price > 0) {
                        const calculatedPrice = newItem.price / (1 - margin / 100);
                        newItem.suggestedPrice = parseFloat(calculatedPrice.toFixed(2));
                    } else {
                        newItem.suggestedPrice = newItem.price;
                    }
                } else if (field === 'price') {
                    // price changed: recalculate suggested price from price and margin
                    const price = numValue || 0;
                    if (newItem.suggestedMargin < 100 && price > 0) {
                        const calculatedPrice = price / (1 - newItem.suggestedMargin / 100);
                        newItem.suggestedPrice = parseFloat(calculatedPrice.toFixed(2));
                    } else {
                        newItem.suggestedPrice = price;
                    }
                } else if (field === 'suggestedPrice') {
                    // suggested price changed: recalculate margin from price and suggested price
                    const suggestedPrice = numValue || 0;
                    if (suggestedPrice > 0 && newItem.price > 0) {
                        const calculatedMargin = ((suggestedPrice - newItem.price) / suggestedPrice) * 100;
                        newItem.suggestedMargin = parseFloat(calculatedMargin.toFixed(2));
                    } else {
                        newItem.suggestedMargin = 0;
                    }
                }

                return newItem;
            }
            return item;
        });

        setDataSource(updatedData);

        // Update parent products state
        const updatedProducts = updatedData.map((item) => ({
            productId: item.productId,
            price: parseFloat(item.price) || 0,
            suggestedPrice: parseFloat(item.suggestedPrice) || 0
        }));
        setProducts(updatedProducts);
    };

    const download = () => {
        let titles = ['Producto', 'Producto ID', 'Margen Sugerido', 'Costo'];
        let formatProducts = [];

        if (storeType?.channel === 'moderno') {
            formatProducts = dataSource.map((item) => ({
                Producto: item.productName,
                'Producto ID': item.productId,
                Costo: item.price,
                'Margen Sugerido': item.suggestedMargin
            }));
        } else {
            titles = ['Producto', 'Producto ID', 'Precio Sugerido'];
            formatProducts = dataSource.map((item) => ({
                Producto: item.productName,
                'Producto ID': item.productId,
                'Precio Sugerido': item.suggestedPrice
            }));
        }

        downloadExcel(titles, formatProducts, `Lista de precio ${storeType?.label?.split('.').join(' ')}`);
    };

    return { dataSource, updateProduct, download };
}
